import { configureStore } from '@reduxjs/toolkit';
import { createTransform, persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import rootReducer from './slices/rootReducer';

// WHAT IS SAVED IN THE BROWSER WHILE SIGNED IN (28 Oct, the sign-in build, item 3). The old config spelled the option
// `whiteList`, which redux-persist ignores, so EVERY slice was saved. This is that same set, now spelled `whitelist`
// and written out, so nothing a reload restores changes: search's restore needs `agentic` and `search`, and the Stripe
// return to /checkout/thanks (a full page load) needs `cart`. Narrowing it is a separate decision.
export const PERSISTED_SLICES = ['auth', 'search', 'latest', 'affinity', 'cart', 'customer', 'attempt', 'navigation', 'agentic'];

// Two auth flags are never saved and never restored. authInitialized: a restored `true` let a revoked account's old
// role show a page's frame on a cold load before Firebase answered; now every page waits for the session bridge.
// sessionReady: it is set only after this page's session sync; a restored `true` let the homepage's cookie-only fetches
// fire before that sync (authSlice.ts says it must never survive a reload). A slice's own
// REHYDRATE case cannot do this: redux-persist's merge keeps the saved slice whenever the reducer left it unchanged.
export const authFlagsNotRestored = createTransform(
  (inbound: any) => {
    const { authInitialized, sessionReady, ...rest } = inbound ?? {};
    return rest;
  },
  (outbound: any) => ({ ...outbound, authInitialized: false, sessionReady: false }),
  { whitelist: ['auth'] }
);

export const persistConfig = {
  key: 'root',
  storage,
  whitelist: PERSISTED_SLICES,
  transforms: [authFlagsNotRestored],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});
const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export { store, persistor };
